import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchHospitals } from "../lib/api";
import ExportMenu from "../components/ExportMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function highlight(text, q) {
  if (!text || !q) return text || "";
  try {
    const parts = String(text).split(new RegExp(`(${q.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")})`, "ig"));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase() ? (
        <mark key={i} className="ls-mark">{p}</mark>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  } catch {
    return text;
  }
}

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [count, setCount] = useState(0);

  const runSearch = async (query) => {
    if (!query?.trim()) {
      setResults([]);
      setCount(0);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const res = await searchHospitals(query);
      setResults(res.results || []);
      setCount(res.count || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initial) runSearch(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    setParams({ q });
    runSearch(q);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Hospital Search</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Search by hospital name, alias, or CCN. Entity resolution finds Parkland by every common name.
        </p>
      </div>

      <form onSubmit={onSubmit} className="flex gap-2 max-w-3xl" data-testid="search-form">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. Parkland, Cedars-Sinai, MGH, 450015"
          className="input-brand"
          data-testid="global-search-input"
          autoFocus
        />
        <button type="submit" className="btn-brand" data-testid="search-submit-button">
          Search
        </button>
      </form>

      {err && (
        <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="search-error">
          {err}
        </div>
      )}

      {!err && (
        <div className="mt-6">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500" data-testid="search-results-count">
              {loading ? "searching…" : `${count} result${count === 1 ? "" : "s"}`}
            </div>
            {count > 0 && q && (
              <ExportMenu
                testId="search-export"
                items={[{ label: "Search results", csvUrl: `${API}/hospitals/search.csv?q=${encodeURIComponent(q)}&limit=50`, xlsxUrl: `${API}/hospitals/search.xlsx?q=${encodeURIComponent(q)}&limit=50` }]}
              />
            )}
          </div>
          <table className="w-full data-table" data-testid="search-results-table">
            <thead>
              <tr>
                <th>Score</th>
                <th>CCN</th>
                <th>Legal Name</th>
                <th>City / State</th>
                <th>FY</th>
                <th>Matched alias</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="text-center text-zinc-400 py-6 text-sm">
                    {initial ? "No matches" : "Type a hospital name or CCN to begin"}
                  </td>
                </tr>
              )}
              {results.map((r) => {
                const isSystem = r.result_type === "system";
                const rowKey = isSystem ? `sys-${r.ein}` : r.ccn;
                const goTo = isSystem ? `/systems/${r.ein}` : `/hospitals/${r.ccn}`;
                return (
                <tr
                  key={rowKey}
                  className={isSystem ? "bg-emerald-50/40" : ""}
                  data-testid={isSystem ? `system-search-result-row-${r.ein}` : `hospital-search-result-row-${r.ccn}`}
                  onClick={() => (window.location.href = goTo)}
                >
                  <td className="font-mono text-zinc-600">{r.score}{r.effective_score > r.score && <span className="text-emerald-700"> ↑</span>}</td>
                  <td className="font-mono text-zinc-900 font-medium">
                    {isSystem ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="px-1.5 py-0.5 text-[9px] font-mono rounded bg-emerald-600 text-white">SYSTEM</span>
                        <span className="font-mono">{r.ein}</span>
                      </span>
                    ) : r.ccn}
                  </td>
                  <td>
                    <Link
                      to={goTo}
                      className="text-zinc-900 hover:text-emerald-700 font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {highlight((isSystem ? r.system_name : r.legal_name) || "—", q)}
                    </Link>
                    {isSystem && (
                      <div className="text-[11px] text-emerald-700 mt-1 font-mono" data-testid={`system-member-count-${r.ein}`}>
                        {r.member_count} member hospitals
                        {r.total_revenue && <span className="text-zinc-500"> · TY{r.latest_tax_year} rev ${(r.total_revenue/1e9).toFixed(1)}B</span>}
                      </div>
                    )}
                    {!isSystem && r.aliases && r.aliases.length > 0 && (
                      <div className="text-[11px] text-zinc-500 mt-1 truncate max-w-md">
                        aka: {r.aliases.slice(0, 4).map((a, i) => (
                          <span key={i}>{i > 0 ? " · " : ""}{highlight(a, q)}</span>
                        ))}
                        {r.aliases.length > 4 && <span className="text-zinc-400"> · +{r.aliases.length - 4} more</span>}
                      </div>
                    )}
                  </td>
                  <td className="text-zinc-700">
                    {r.city ? `${r.city}` : "—"}
                    {r.state && <span className="text-zinc-400">, {r.state}</span>}
                  </td>
                  <td className="font-mono text-zinc-700">{isSystem ? (r.latest_tax_year ? `TY${r.latest_tax_year}` : "—") : `FY${r.latest_fy || "—"}`}</td>
                  <td className="text-zinc-500 text-xs">{isSystem ? "parent system" : (r.matched_alias || "")}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
